import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { FsBadgeModule } from '@firestitch/badge';
import { FsDateModule } from '@firestitch/date';
import { FsDialogModule } from '@firestitch/dialog';
import { FsListComponent, FsListConfig, FsListModule } from '@firestitch/list';

import { map, tap } from 'rxjs/operators';

import { AccountData } from '@common/data';
import { SessionService } from '@common/services';

import { ObjectDrawerService } from '@libs/drawers/drawer';
import { ObjectIdentifierComponent } from '@libs/object-identifier/components';

import { NotificationService } from '../../../services';


@Component({
  templateUrl: './notifications-menu.component.html',
  styleUrls: ['./notifications-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    FsListModule,
    FsBadgeModule,
    FsDateModule,
    FsDialogModule,
    ObjectIdentifierComponent,
  ],
})
export class NotificationsMenuComponent implements OnInit {
  public data = inject(MAT_DIALOG_DATA);
  
  @ViewChild(FsListComponent) 
  public listComponent: FsListComponent;

  public config: FsListConfig;
  
  private _sessionService = inject(SessionService);
  private _accountData = inject(AccountData);
  private _objectDrawerService = inject(ObjectDrawerService);
  private _notificationService = inject(NotificationService);
  private _dialogRef = inject<MatDialogRef<NotificationsMenuComponent>>(MatDialogRef);

  public ngOnInit(): void {
    this.config = {
      loadMore: true,
      queryParam: false,
      status: false,
      reload: false,
      style: 'card',
      rowHoverHighlight: false,
      fetch: (query) => {
        query = {
          ...query,
          objects: true,
          objectIdentifiers: true,
          projects: true,
          projectAvatars: true,
          limit: 15,
          environmentId: this._sessionService.environmentId(),
          order: 'createDate,desc',
        };

        return this._accountData.notifications(query, { key: null })
          .pipe(
            map((data) => {
              data.notifications.forEach((notification) => this._map(notification));

              return { data: data.notifications, paging: data.paging };
            }),
            tap(() => {
              setTimeout(() => {
                this._dialogRef.updatePosition();
              });
            }),
          );
      },
    };
  }

  public markReadAll(): void {
    this._accountData.notificationsReadAll()
      .subscribe(() => {
        this.listComponent.getData()
          .forEach((notification) => {
            notification.read = true;
            this.listComponent.updateData(notification, (item) => {
              return item.id === notification.id;
            });
          });
        this._notificationService.reload();
      });
  }

  public toggleNotification(event: Event, notification, read: boolean): void {
    event.stopPropagation();
    if (read) {
      this.markUnread(notification.id);
    } else {
      this.markRead(notification);
    }
  }

  public markUnread(notificationId: number): void {
    this._accountData.notificationsUnread({ notificationId })
      .subscribe(() => {
        this.listComponent.reload();
        this._notificationService.reload();
      });
  }

  public markRead(notification): void {
    this._accountData.notificationsRead(notification.id)
      .subscribe((_notification) => {
        this._notificationService.reload();
        this.listComponent.updateData(this._map(_notification), (data: any) => {
          return data.id === _notification.id;
        });
      });
  }

  public openObject(notification): void {
    this.markRead(notification);
    this._objectDrawerService.openObject(notification.object.identifier);
  }

  public close(): void {
    this._dialogRef.close();
  }

  private _map(notification) {
    if (notification.object) {
      notification.object.project = notification.project;
    }
    notification.message = `${notification.message || ''}`.trim();

    return notification;
  }

}
